import { BasketDetailsModel } from 'models/Basket';
import { PaymentDetailsModel, PaymentTypes } from 'models/Payment';
import { api } from 'repositories/api';
import { basketStorage } from 'storages';
import { logsHelper, errorToBasketErrorModel } from 'helpers';
import { routes } from 'routes';
import { PageViewOverridesModel } from 'models/Analytics/PageViewOverridesModel';

export enum GtmCheckoutSteps {
    Basket = 1,
    DeliveryDetails = 2,
    DeliveryDate = 3,
    PaymentOrDpn = 4,
    FinanceFormStepOne = 5,
    FinanceFormStepTwo = 6,
    FinanceFormStepThree = 7,
    FinanceFormStepFour = 8,
    OrderProcessing = 9,
    PaymentError = 10,
    Purchase = 11
}

export enum ExponeaCheckoutSteps {
    DeliveryDetails = 1,
    DeliveryDate = 2,
    DataProtectionNotice = 3,
    Finance = 4,
    FinanceProcessing = 5,
    Payment = 6,
    OrderProcessing = 7,
    Summary = 8
}

export enum GtmEmptyEvents {
    FinanceApplicationDeclided = 'checkout_declined_finance',
    FinanceApplicationIncorrect = 'checkout_incorrect_finance_details'
}

export class AnalyticsHelper {
    public sendToGoogleMessageAboutPaymentError(basket: BasketDetailsModel | null, paymentDetails: PaymentDetailsModel): void {
        try {
            if (basket && paymentDetails.paymentType !== PaymentTypes.None) {
                // cloned from old code - marking payment error as moving to some checkout step
                api.google.checkoutStep(
                    GtmCheckoutSteps.PaymentError,
                    basket.guid,
                    basket.entries,
                    basket.leadTime,
                    paymentDetails.paymentType,
                    basket.user.uid);
            }
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGoogleMessageAboutPaymentError'));
        }
    }

    public sendToGoogleMessageAboutInputChangeCompleted(inputName: string, formName: string): void {
        try {
            const basket = basketStorage.getBasket()!;
            api.google.fieldEvent(inputName, formName, basket.guid);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGoogleMessageAboutInputChangeCompleted'));
        }
    }

    public sendToGooglePaymentMethodChange(selectedMethod: 'card' | 'paypal'): void {
        try {
            const basket = basketStorage.getBasket()!;
            api.google.fieldEvent(selectedMethod + 'PaymentSelected', 'card', basket.guid);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGooglePaymentMethodChange'));
        }
    }

    public sendToGooglePaypalPaymentStatus(status: string): void {
        try {
            const basket = basketStorage.getBasket()!;
            api.google.fieldEvent(status, 'paypal', basket.guid);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGooglePaypalPaymentStatus'));
        }
    }

    public sendToGoogleDefaultPageViewMessage(location: string): void {
        try {
            if (!this.isDefaultPageViewEventAcceptableLocation(location)) {
                return;
            }

            const category = this.getDefaultPageViewCategoryByLocation(location);
            const subCategory = this.getDefaultPageViewSubCategoryByLocation(location);

            api.google.checkoutPageViewStep(location, category, subCategory);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGooglePageViewMessage'));
        }
    }

    public sendToGoogleCustomPageViewMessage(location: string, overrides?: PageViewOverridesModel): void {
        try {
            if (!this.isCustomPageViewEventAcceptableLocation(location)) {
                return;
            }

            const category = overrides?.category ?? this.getDefaultPageViewCategoryByLocation(location);
            const subCategory = overrides?.subCategory ?? this.getDefaultPageViewSubCategoryByLocation(location);
            const pageType = overrides?.pageType ?? 'checkout';

            api.google.checkoutPageViewStep(location, category, subCategory, pageType);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, 'AnalyticsHelper:sendToGooglePageViewMessage'));
        }
    }

    public withLogging(func: Function, source: string): void {
        try {
            func();
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, `${source}:sendAnalytics`));
        }
    }

    public* withLoggingGenerator(func: Function, source: string): Generator<any, void, any> {
        try {
            yield func();
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, `${source}:sendAnalytics`));
        }
    }

    public sendToGoogleLoginMessage(location: string, eventName: string, additionalParams: any = undefined) {
        try {
            api.google.createLoginProcessEvent(location, eventName, additionalParams);
        } catch (error) {
            logsHelper.error(errorToBasketErrorModel(error, `${location}:sendAnalytics`));
        }
    }

    private isDefaultPageViewEventAcceptableLocation(location: string) {
        const allowedPageViewEventLocation: string[] = [
            routes.deliveryDate.path,
            routes.payment.path,
            routes.dataProtectionNotice.path,
            routes.orderSummaryPage.path,
            routes.financeApplication.path,
            routes.financeApplicationStepTwo.path,
            routes.financeApplicationStepThree.path,
            routes.financeApplicationStepFour.path
        ];

        return allowedPageViewEventLocation.includes(location);
    }

    private isCustomPageViewEventAcceptableLocation(location: string) {
        const allowedPageViewEventLocation: string[] = [
            routes.deliveryDetails.path
        ];

        return allowedPageViewEventLocation.includes(location);
    }

    private getDefaultPageViewSubCategoryByLocation(location: string): string {
        switch (location) {
            case routes.deliveryDetails.path:
                return 'Delivery Address';
            case routes.deliveryDate.path:
                return 'Delivery Option';
            case routes.payment.path:
                return 'Payment';
            case routes.dataProtectionNotice.path:
                return 'Finance - Data Protection Notice';
            case routes.financeApplication.path:
                return 'Finance - Customer Details';
            case routes.financeApplicationStepTwo.path:
                return 'Finance - Address Details';
            case routes.financeApplicationStepThree.path:
                return 'Finance - Employment Details';
            case routes.financeApplicationStepFour.path:
                return 'Finance - Bank Details';
            case routes.orderSummaryPage.path:
                return 'Purchase';
            default:
                return 'Unknown page view subcategory';
        }
    }

    private getDefaultPageViewCategoryByLocation(location: string): string {
        switch (location) {
            case routes.deliveryDetails.path:
            case routes.deliveryDate.path:
            case routes.payment.path:
            case routes.dataProtectionNotice.path:
            case routes.financeApplication.path:
            case routes.financeApplicationStepTwo.path:
            case routes.financeApplicationStepThree.path:
            case routes.financeApplicationStepFour.path:
                return 'Checkout';
            case routes.orderSummaryPage.path:
                return 'Purchase';
            default:
                return 'Unknown page view category';
        }
    }
}

export const analyticsHelper = new AnalyticsHelper();